import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { MapPin, Car, Hotel, Camera, DollarSign, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

export function BudgetResult({ result, convertedResult, currencyMode, convertingCurrency, onToggleCurrency }) {
  const d = currencyMode === 'converted' && convertedResult ? convertedResult : result;

  return (
    <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl" data-testid="budget-result">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-serif text-xl text-ocean flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-gold" /> Estimated Budget
          </CardTitle>
          <Button variant="outline" size="sm" onClick={onToggleCurrency} disabled={convertingCurrency}
            className="text-xs border-ocean/30 text-ocean hover:bg-ocean/10 h-8"
            data-testid="calc-currency-toggle-btn">
            {convertingCurrency ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <span className="mr-1">&#8644;</span>}
            {currencyMode === 'original' ? 'Home Currency' : 'Original'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {d.total && (
          <div className="bg-gradient-to-r from-ocean/10 to-gold/10 rounded-xl p-5 text-center border border-ocean/20">
            <p className="text-sm text-ocean/60 uppercase tracking-wide">Estimated Total</p>
            <p className="font-serif text-3xl text-ocean font-bold mt-1">{d.total}</p>
            {d.currency && <p className="text-xs text-ocean/50 mt-1">{d.currency}</p>}
            {d.per_person && <p className="text-sm text-ocean/70 mt-1">{d.per_person} per person</p>}
          </div>
        )}

        <BudgetBreakdownList data={d} />

        {d.per_destination && Array.isArray(d.per_destination) && (
          <div className="mt-4 pt-4 border-t border-ocean/10">
            <p className="text-sm font-medium text-ocean mb-3">Per Destination</p>
            {d.per_destination.map((dest, i) => (
              <div key={`perdest-${dest.destination || i}`} className="p-3 bg-gold/5 rounded-lg mb-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-charcoal flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-gold" /> {dest.destination}
                  </span>
                  <span className="font-semibold text-ocean">{dest.total}</span>
                </div>
                {dest.days && <p className="text-xs text-charcoal/50 mt-0.5">{dest.days} days</p>}
              </div>
            ))}
          </div>
        )}

        {result.tips && Array.isArray(result.tips) && (
          <div className="mt-4 pt-4 border-t border-ocean/10">
            <p className="text-sm font-medium text-ocean mb-2">Budget Tips</p>
            <ul className="space-y-1">
              {result.tips.map((tip, i) => (
                <li key={`tip-${i}`} className="text-xs text-charcoal/70 flex gap-2">
                  <span className="text-gold shrink-0">&#9733;</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result.note && (
          <p className="text-xs text-charcoal/50 italic mt-3">{result.note}</p>
        )}
      </CardContent>
    </Card>
  );
}

function BudgetBreakdownList({ data }) {
  const items = [
    { key: 'accommodation', icon: <Hotel className="w-4 h-4 text-ocean" />, label: 'Accommodation' },
    { key: 'transport', icon: <Car className="w-4 h-4 text-ocean" />, label: 'Transport' },
    { key: 'food', icon: <span className="text-base">&#127860;</span>, label: 'Food & Dining' },
    { key: 'activities', icon: <Camera className="w-4 h-4 text-ocean" />, label: 'Activities & Sightseeing' },
    { key: 'miscellaneous', icon: <span className="text-base">&#128176;</span>, label: 'Miscellaneous' },
  ];

  return (
    <div className="space-y-3">
      {items.map(item => data[item.key] ? (
        <div key={item.key} className="flex items-center justify-between p-3 bg-sky/5 rounded-lg">
          <div className="flex items-center gap-2">
            {item.icon}
            <span className="text-sm text-charcoal">{item.label}</span>
          </div>
          <span className="font-semibold text-ocean">{data[item.key]}</span>
        </div>
      ) : null)}
    </div>
  );
}

export function BudgetPlaceholder() {
  return (
    <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl">
      <CardContent className="p-12 text-center">
        <DollarSign className="w-16 h-16 text-ocean/20 mx-auto mb-4" />
        <h3 className="font-serif text-xl text-ocean/50 mb-2">Budget Estimation</h3>
        <p className="text-ocean/40 text-sm">Fill in your trip details and click Calculate to get an AI-powered budget estimation.</p>
      </CardContent>
    </Card>
  );
}

export function OptionGrid({ options, selected, onSelect, testIdPrefix }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map(opt => (
        <button key={opt.value}
          onClick={() => onSelect(opt.value)}
          className={`p-2.5 rounded-lg text-xs text-left border transition-all ${selected === opt.value ? 'bg-ocean/10 border-ocean text-ocean font-medium' : 'border-gray-200 text-gray-600 hover:border-ocean/30'}`}
          data-testid={`${testIdPrefix}-${opt.value}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
